import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchCasaMapaPropertyLinking,
  resolveCasaMapaPropertyLinkMatch,
} from "../../../api/clientCasamapaAdminApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useCurrency, useLocale } from "../../../intl";
import "../../../styles/casamapaAdminPortal.css";

const COPY = "realEstate.casaMapa.admin.propertyLinking";

const COMPARE_FIELDS = [
  { key: "price", kind: "money", tolerancePct: 0.05 },
  { key: "bedrooms", kind: "number" },
  { key: "bathrooms", kind: "number" },
  { key: "areaM2", kind: "number", tolerancePct: 0.1 },
  { key: "propertyType", kind: "text" },
  { key: "listingType", kind: "text" },
  { key: "city", kind: "text" },
  { key: "neighborhood", kind: "text" },
  { key: "address", kind: "text" },
  { key: "title", kind: "text" },
];

function Metric({ label, value, hint, tone }) {
  return (
    <article className={`cm-admin-analytics__metric${tone ? ` cm-admin-analytics__metric--${tone}` : ""}`}>
      <span className="cm-admin-analytics__metric-label">{label}</span>
      <strong className="cm-admin-analytics__metric-value">{value}</strong>
      {hint ? <small className="cm-admin-analytics__metric-hint">{hint}</small> : null}
    </article>
  );
}

function placeLabel(row) {
  return [row.neighborhood, row.city].filter(Boolean).join(", ") || "—";
}

function formatReason(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function openUrls(urls) {
  [...new Set(urls.filter(Boolean))].forEach((url, index) => {
    window.setTimeout(() => window.open(url, "_blank", "noopener,noreferrer"), index * 120);
  });
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function valuesDiffer(left, right, field) {
  const a = left?.[field.key];
  const b = right?.[field.key];
  if (a == null && b == null) return false;
  if (a == null || b == null) return true;
  if (field.kind === "number" || field.kind === "money") {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isFinite(na) || !Number.isFinite(nb)) return true;
    if (field.tolerancePct) {
      const base = Math.max(Math.abs(na), Math.abs(nb), 1);
      return Math.abs(na - nb) / base > field.tolerancePct;
    }
    return na !== nb;
  }
  return normalizeText(a) !== normalizeText(b);
}

function findInconsistentKeys(records = []) {
  if (records.length < 2) return new Set();
  const mismatched = new Set();
  for (const field of COMPARE_FIELDS) {
    const baseline = records[0];
    if (records.slice(1).some((record) => valuesDiffer(baseline, record, field))) {
      mismatched.add(field.key);
    }
  }
  return mismatched;
}

function PropertyPhoto({ url, alt }) {
  if (!url) {
    return <div className="cm-link-photo cm-link-photo--empty" aria-hidden="true" />;
  }
  return (
    <div className="cm-link-photo">
      <img src={url} alt={alt || ""} loading="lazy" referrerPolicy="no-referrer" />
    </div>
  );
}

function CompareFieldRow({ label, values, mismatched, formatValue }) {
  return (
    <div className={`cm-link-compare-row${mismatched ? " cm-link-compare-row--mismatch" : ""}`}>
      <div className="cm-link-compare-row__label">
        {label}
        {mismatched ? <span className="cm-link-mismatch-tag">≠</span> : null}
      </div>
      <div className="cm-link-compare-row__values" style={{ "--cm-link-cols": values.length }}>
        {values.map((value, index) => (
          <div key={index} className="cm-link-compare-row__value">
            {formatValue(value)}
          </div>
        ))}
      </div>
    </div>
  );
}

function PropertyComparePanel({
  records,
  inconsistentKeys,
  money,
  t,
  path,
  formatArea,
}) {
  if (!records?.length) return null;

  const fieldLabel = (key) => t(`${COPY}.fields.${key}`);

  return (
    <div className="cm-link-compare">
      <div className="cm-link-compare__photos" style={{ "--cm-link-cols": records.length }}>
        {records.map((record) => (
          <article key={record.id || record.propertyId || record.canonicalListingId} className="cm-link-compare__card">
            <PropertyPhoto url={record.primaryImageUrl} alt={record.title || ""} />
            <div className="cm-link-compare__card-head">
              <strong>{record.roleLabel}</strong>
              <span className="muted">{record.agencyName || record.sourceKey || "—"}</span>
            </div>
            <div className="cm-link-compare__card-links">
              {record.sourceUrl ? (
                <a href={record.sourceUrl} target="_blank" rel="noreferrer">
                  {t(`${COPY}.openSource`)}
                </a>
              ) : null}
              {record.canonicalListingId ? (
                <Link to={path(`listings/${record.canonicalListingId}`)}>{t(`${COPY}.openUnique`)}</Link>
              ) : null}
              {record.agencySlug ? (
                <Link to={path(`agencies/${record.agencySlug}`)}>{record.agencyName}</Link>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      {inconsistentKeys.size ? (
        <p className="cm-link-compare__alert">
          {t(`${COPY}.inconsistenciesFound`, { count: inconsistentKeys.size })}
        </p>
      ) : (
        <p className="cm-link-compare__ok muted">{t(`${COPY}.noInconsistencies`)}</p>
      )}

      <div className="cm-link-compare__fields">
        {COMPARE_FIELDS.map((field) => (
          <CompareFieldRow
            key={field.key}
            label={fieldLabel(field.key)}
            values={records.map((record) => record[field.key])}
            mismatched={inconsistentKeys.has(field.key)}
            formatValue={(value) => {
              if (value == null || value === "") return "—";
              if (field.kind === "money") return money(value);
              if (field.key === "areaM2") return formatArea(value);
              return String(value);
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default function CasaMapaAdminPropertyLinkingPage() {
  const { clientViewId, path } = useCasaMapaAdminPortal();
  const { t, locale } = useLocale();
  const { formatMoney } = useCurrency();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [expandedId, setExpandedId] = useState("");

  const load = useCallback(async () => {
    if (!clientViewId) return;
    setLoading(true);
    setError("");
    try {
      setReport(await fetchCasaMapaPropertyLinking(clientViewId));
    } catch (err) {
      setReport(null);
      setError(err.message || t(`${COPY}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = report?.summary;
  const rules = report?.rules;
  const count = (value) => (value == null ? "—" : Number(value).toLocaleString(locale));
  const money = (value) => (value == null ? "—" : formatMoney(value));
  const formatArea = (value) =>
    value == null ? "—" : `${Number(value).toLocaleString(locale)} m²`;

  const nextStep = useMemo(() => {
    if (!summary) return "";
    if (summary.needsReview > 0) return t(`${COPY}.nextReview`, { count: count(summary.needsReview) });
    if (summary.multiAgencyProperties > 0) {
      return t(`${COPY}.nextMultiAgency`, { count: count(summary.multiAgencyProperties) });
    }
    return t(`${COPY}.nextClear`);
  }, [count, summary, t]);

  async function decide(matchCandidateId, action) {
    if (!clientViewId) return;
    setBusyId(matchCandidateId);
    setActionError("");
    try {
      await resolveCasaMapaPropertyLinkMatch(clientViewId, matchCandidateId, action);
      await load();
    } catch (err) {
      setActionError(err.message || t(`${COPY}.actionError`));
    } finally {
      setBusyId("");
    }
  }

  return (
    <div className="casamapa-admin-page">
      <header className="casamapa-admin-page__header">
        <div>
          <div className="eyebrow">{t(`${COPY}.eyebrow`)}</div>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="muted">{t(`${COPY}.subtitle`)}</p>
        </div>
        <button type="button" className="midpoint-share-button" onClick={load} disabled={loading}>
          {t("realEstate.casaMapa.admin.refresh")}
        </button>
      </header>

      {error ? <p className="form-error">{error}</p> : null}
      {loading && !report ? <p className="muted">{t("realEstate.casaMapa.admin.loading")}</p> : null}

      {report ? (
        <>
          <p className="cm-link-guide">{nextStep}</p>

          <div className="cm-admin-analytics__metric-grid">
            <Metric
              label={t(`${COPY}.uniqueProperties`)}
              value={count(summary.uniqueProperties)}
              hint={t(`${COPY}.uniqueHint`)}
              tone="blue"
            />
            <Metric
              label={t(`${COPY}.multiAgency`)}
              value={count(summary.multiAgencyProperties)}
              hint={t(`${COPY}.multiAgencyMetricHint`)}
              tone="teal"
            />
            <Metric
              label={t(`${COPY}.needsReview`)}
              value={count(summary.needsReview)}
              hint={t(`${COPY}.scoreBand`, { count: count(summary.scoreBands.review) })}
            />
            <Metric
              label={t(`${COPY}.sourceLinks`)}
              value={count(summary.activeSourceLinks)}
              hint={t(`${COPY}.autoMatchedHint`, { count: count(summary.autoMatched) })}
            />
          </div>

          <details className="cm-link-rules">
            <summary>{t(`${COPY}.howTitle`)}</summary>
            <div className="cm-link-rules__body">
              <p>{rules.identity}</p>
              <p>{rules.multiAgency}</p>
              <p>{rules.availability}</p>
              <p>{rules.priceMonitoring}</p>
              <p className="muted">
                {t(`${COPY}.thresholds`, {
                  auto: rules.autoMatchThreshold,
                  review: rules.reviewThreshold,
                  stale: rules.lifecycle.staleAfterMisses,
                  inactive: rules.lifecycle.inactiveAfterMisses,
                  retired: rules.lifecycle.retireAfterMisses,
                })}
              </p>
              <div className="casamapa-admin-table-wrap">
                <table className="casamapa-admin-table">
                  <thead>
                    <tr>
                      <th>{t(`${COPY}.signal`)}</th>
                      <th>{t(`${COPY}.points`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.signals.map((signal) => (
                      <tr key={signal.id}>
                        <td>{signal.label}</td>
                        <td>{signal.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </details>

          <section className="panel-card casamapa-admin-section" id="property-linking-review">
            <h2>{t(`${COPY}.reviewTitle`)}</h2>
            <p className="muted">{t(`${COPY}.reviewHint`)}</p>
            {actionError ? <p className="form-error">{actionError}</p> : null}
            {report.reviewQueue.length ? (
              <div className="cm-link-review-list">
                {report.reviewQueue.map((row) => {
                  const source = {
                    ...(row.source || {}),
                    id: `source-${row.matchCandidateId}`,
                    roleLabel: t(`${COPY}.sourceListing`),
                    title: row.source?.title || row.sourceTitle,
                    price: row.source?.price ?? row.sourcePrice,
                    sourceUrl: row.source?.sourceUrl || row.sourceUrl,
                    agencyName: row.source?.agencyName || row.sourceKey,
                    sourceKey: row.sourceKey,
                  };
                  const canonical = {
                    ...(row.canonical || {}),
                    id: `canonical-${row.matchCandidateId}`,
                    roleLabel: t(`${COPY}.candidate`),
                    title: row.canonical?.title || row.canonicalTitle,
                    city: row.canonical?.city || row.city,
                    neighborhood: row.canonical?.neighborhood || row.neighborhood,
                    canonicalListingId: row.canonicalListingId,
                  };
                  const pair = [source, canonical];
                  const inconsistentKeys = findInconsistentKeys(pair);
                  return (
                    <article key={row.matchCandidateId} className="cm-link-review cm-link-review--compare">
                      <div className="cm-link-review__copy">
                        <strong>{t(`${COPY}.reviewQuestion`)}</strong>
                        <p className="muted">
                          {t(`${COPY}.score`)}: {row.score}
                          {row.reason ? ` · ${formatReason(row.reason)}` : ""}
                        </p>
                        <PropertyComparePanel
                          records={pair}
                          inconsistentKeys={inconsistentKeys}
                          money={money}
                          t={t}
                          path={path}
                          formatArea={formatArea}
                        />
                      </div>
                      <div className="cm-link-review__actions">
                        <button
                          type="button"
                          className="midpoint-share-button"
                          disabled={Boolean(busyId)}
                          onClick={() => decide(row.matchCandidateId, "CONFIRM_MATCH")}
                        >
                          {busyId === row.matchCandidateId ? t(`${COPY}.working`) : t(`${COPY}.confirmSame`)}
                        </button>
                        <button
                          type="button"
                          className="casamapa-admin-link-button"
                          disabled={Boolean(busyId)}
                          onClick={() => decide(row.matchCandidateId, "REJECT_MATCH")}
                        >
                          {t(`${COPY}.notTheSame`)}
                        </button>
                        <button
                          type="button"
                          className="casamapa-admin-link-button"
                          disabled={Boolean(busyId)}
                          onClick={() => decide(row.matchCandidateId, "IGNORE")}
                        >
                          {t(`${COPY}.skipForNow`)}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="muted">{t(`${COPY}.emptyReview`)}</p>
            )}
          </section>

          <section className="panel-card casamapa-admin-section" id="property-linking-multi">
            <h2>{t(`${COPY}.multiAgencyTitle`)}</h2>
            <p className="muted">{t(`${COPY}.multiAgencyHint`)}</p>
            {report.multiAgencyProperties.length ? (
              <div className="cm-link-multi-list">
                {report.multiAgencyProperties.map((row) => {
                  const open = expandedId === row.canonicalListingId;
                  const records = (row.records || []).map((record, index) => ({
                    ...record,
                    id: record.propertyId || `${row.canonicalListingId}-${index}`,
                    roleLabel: record.agencyName || t(`${COPY}.sourceListing`),
                  }));
                  const sourceUrls = records.map((record) => record.sourceUrl);
                  const inconsistentKeys = findInconsistentKeys(records);
                  return (
                    <article
                      key={row.canonicalListingId}
                      className={`cm-link-card${open ? " cm-link-card--open" : ""}`}
                    >
                      <div className="cm-link-card__head">
                        <div>
                          <Link to={path(`listings/${row.canonicalListingId}`)}>
                            <strong>{row.title || t(`${COPY}.untitled`)}</strong>
                          </Link>
                          <div className="muted">
                            {placeLabel(row)} · {money(row.price)} · {row.availabilityStatus || "—"}
                          </div>
                          <div className="cm-link-chip-row">
                            {records.length
                              ? records.map((record) =>
                                  record.sourceUrl ? (
                                    <a
                                      key={record.propertyId}
                                      className="cm-link-chip"
                                      href={record.sourceUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {record.agencyName}
                                    </a>
                                  ) : (
                                    <span key={record.propertyId} className="cm-link-chip cm-link-chip--plain">
                                      {record.agencyName}
                                    </span>
                                  )
                                )
                              : (row.sources || []).map((source) => (
                                  <span key={source} className="cm-link-chip cm-link-chip--plain">
                                    {source}
                                  </span>
                                ))}
                          </div>
                        </div>
                        <div className="cm-link-card__actions">
                          <Link className="casamapa-admin-link-button" to={path(`listings/${row.canonicalListingId}`)}>
                            {t(`${COPY}.openUnique`)}
                          </Link>
                          <a
                            className="casamapa-admin-link-button"
                            href={`/v/casamapa/listings/${row.canonicalListingId}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t("realEstate.casaMapa.admin.viewPublic")}
                          </a>
                          <button
                            type="button"
                            className="midpoint-share-button"
                            disabled={!sourceUrls.some(Boolean)}
                            onClick={() => openUrls(sourceUrls)}
                          >
                            {t(`${COPY}.openAllSources`, { count: records.length || row.sourceCount })}
                          </button>
                          <button
                            type="button"
                            className="casamapa-admin-link-button"
                            aria-expanded={open}
                            onClick={() =>
                              setExpandedId((current) =>
                                current === row.canonicalListingId ? "" : row.canonicalListingId
                              )
                            }
                          >
                            {open ? t(`${COPY}.hideRecords`) : t(`${COPY}.showRecords`)}
                          </button>
                        </div>
                      </div>
                      {open ? (
                        records.length ? (
                          <PropertyComparePanel
                            records={records}
                            inconsistentKeys={inconsistentKeys}
                            money={money}
                            t={t}
                            path={path}
                            formatArea={formatArea}
                          />
                        ) : (
                          <p className="muted">{t(`${COPY}.emptyRecords`)}</p>
                        )
                      ) : null}
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="muted">{t(`${COPY}.emptyMultiAgency`)}</p>
            )}
          </section>

          <details className="cm-link-rules">
            <summary>{t(`${COPY}.availabilityTitle`)}</summary>
            <div className="cm-link-rules__body">
              <div className="casamapa-admin-table-wrap">
                <table className="casamapa-admin-table">
                  <thead>
                    <tr>
                      <th>{t(`${COPY}.status`)}</th>
                      <th>{t(`${COPY}.count`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.availability.map((row) => (
                      <tr key={row.status}>
                        <td>{row.status}</td>
                        <td>{count(row.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </details>
        </>
      ) : null}
    </div>
  );
}
